import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, ArrowLeftRight, Loader2, Pencil, Trash2, X } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { DateTimeFields } from "./DateTimeFields";
import { EASE } from "@/components/Motion";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-neutral-200 placeholder:text-neutral-600 focus:border-gold focus:outline-none transition-colors";

export default function AdminTransactions() {
  const [txns, setTxns] = useState(null);
  const [search, setSearch] = useState("");
  const [txnEdit, setTxnEdit] = useState(null);
  const [txnDel, setTxnDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (q = "") =>
    aapi.get("/transactions", { params: q ? { search: q } : {} })
      .then(({ data }) => setTxns(data))
      .catch(() => setTxns([]));

  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), search.trim() ? 350 : 0);
    return () => clearTimeout(t);
  }, [search]);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const saveTxnDate = async () => {
    if (!txnEdit?.date) return;
    setBusy(true);
    try {
      await aapi.put(`/transactions/${txnEdit.txn_id}`, { created_at: new Date(txnEdit.date).toISOString() });
      toast.success(`Transaction ${txnEdit.txn_id} re-dated — audit logged`);
      setTxnEdit(null);
      load(search.trim());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteTxn = async (reverse) => {
    setBusy(true);
    try {
      await aapi.delete(`/transactions/${txnDel.txn_id}?reverse=${reverse}`);
      toast.success(reverse ? "Transaction removed — wallet effect reversed" : "Transaction record removed");
      setTxnDel(null);
      load(search.trim());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-transactions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-neutral-50 sm:text-3xl">
            <span className="font-serif italic text-gold-grad">Transactions</span>
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Every wallet entry across all users — re-date or remove any one of them. All actions are audit-logged.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search TXN ID, user email, note…"
            className={`${inputCls} pl-11`}
            data-testid="admin-transactions-search"
          />
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
        className="mt-6 rounded-2xl border border-white/10 bg-panel/80 p-4 sm:p-6">
        {txns === null ? (
          <div className="flex justify-center py-20">
            <Loader2 size={26} className="animate-spin text-gold" data-testid="transactions-loading" />
          </div>
        ) : txns.length === 0 ? (
          <p className="py-16 text-center text-sm text-neutral-600" data-testid="transactions-empty">No transactions found.</p>
        ) : (
          <div className="space-y-2" data-testid="transactions-list">
            <p className="px-1 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
              {txns.length} most recent{search.trim() ? " matching" : ""}
            </p>
            {txns.map((t) => (
              <div key={t.id} className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3" data-testid={`txn-row-${t.txn_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-gold/90">{t.txn_id}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        t.type === "credit"
                          ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                          : "border-rose-400/30 bg-rose-400/10 text-rose-300"
                      }`}>
                        {t.type} {inr(t.amount)}
                      </span>
                      <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-neutral-500">{t.method}</span>
                    </div>
                    <div className="mt-1 truncate text-xs text-neutral-400">
                      <span className="text-neutral-300">{t.user_email}</span> · {t.note}
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] text-neutral-600">{fmtDate(t.created_at)} · balance after {inr(t.balance_after ?? 0)}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => { setTxnDel(null); setTxnEdit({ txn_id: t.txn_id, date: toLocalInput(t.created_at) }); }}
                      data-testid={`txn-edit-${t.txn_id}`} aria-label="Edit date"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-500 transition-colors hover:border-gold/50 hover:text-gold">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => { setTxnEdit(null); setTxnDel(t); }}
                      data-testid={`txn-delete-${t.txn_id}`} aria-label="Delete"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-500 transition-colors hover:border-rose-400/50 hover:text-rose-300">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {txnEdit?.txn_id === t.txn_id && (
                  <div className="mt-3 rounded-xl border border-gold/20 bg-gold/[0.04] p-4" data-testid={`txn-editor-${t.txn_id}`}>
                    <DateTimeFields value={txnEdit.date} onChange={(v) => setTxnEdit({ ...txnEdit, date: v })} testid={`txn-date-${t.txn_id}`} />
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={saveTxnDate} disabled={busy} data-testid={`txn-date-save-${t.txn_id}`}
                        className="flex items-center gap-2 rounded-lg border border-gold/40 px-4 py-2 text-xs font-semibold text-goldlight hover:bg-gold hover:text-black disabled:opacity-50">
                        {busy && <Loader2 size={12} className="animate-spin" />} Save date
                      </button>
                      <button onClick={() => setTxnEdit(null)} aria-label="Cancel"
                        className="rounded-lg border border-white/10 px-3 py-2 text-xs text-neutral-500 hover:text-neutral-200">
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {txnDel?.txn_id === t.txn_id && (
                  <div className="mt-3 rounded-xl border border-rose-400/30 bg-rose-400/10 p-4" data-testid={`txn-confirm-${t.txn_id}`}>
                    <p className="text-xs leading-relaxed text-rose-200">
                      Delete <span className="font-mono font-semibold">{t.txn_id}</span> ({t.type} {inr(t.amount)} for {t.user_email})?
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => deleteTxn(true)} disabled={busy} data-testid={`txn-delete-reverse-${t.txn_id}`}
                        className="rounded-lg border border-rose-400/40 px-3.5 py-2 text-xs font-semibold text-rose-200 hover:bg-rose-400/20 disabled:opacity-50">
                        Delete + reverse wallet
                      </button>
                      <button onClick={() => deleteTxn(false)} disabled={busy} data-testid={`txn-delete-keep-${t.txn_id}`}
                        className="rounded-lg border border-white/15 px-3.5 py-2 text-xs font-semibold text-neutral-300 hover:border-white/30 disabled:opacity-50">
                        Delete record only
                      </button>
                      <button onClick={() => setTxnDel(null)}
                        className="rounded-lg px-3 py-2 text-xs text-neutral-500 hover:text-neutral-200">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
